import './_digital-exam-page.scss'
import React, { useState } from 'react'
import axios from 'axios'
import { Button } from 'components/elements/button/button'
import { ErrorMessage } from 'components/elements/error-message/error-message'
import { Heading } from 'components/elements/heading/heading'
import { ColorProvider } from 'context/themeContext'
import {
  DigitalExamPageProps,
  DigitalExamQuestion as DigitalExamQuestionType,
  DigitalExamResult,
} from './digitalExamPage.types'
import { DigitalExamQuestion } from './digitalExamQuestion'
import { DigitalExamQuestionNav } from './digitalExamQuestionNav'
import { DigitalExamResults } from './digitalExamResults'

export const DigitalExamPage = ({
  languageCode,
  questionsEndpoint,
  submitEndpoint,
  text,
}: DigitalExamPageProps) => {
  const [mode, setMode] = useState<'intro' | 'questions' | 'result'>('intro')
  const [questions, setQuestions] = useState<DigitalExamQuestionType[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [result, setResult] = useState<DigitalExamResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const startExam = async () => {
    setErrorMessage('')
    setIsLoading(true)

    try {
      const response = await axios.get(questionsEndpoint, {
        params: { languageCode },
      })

      if (!response.data.success) {
        setErrorMessage(text.genericErrorText)
        return
      }

      setQuestions(response.data.value.questions)
      setAnswers({})
      setCurrentIndex(0)
      setResult(null)
      setMode('questions')
    } catch (error) {
      setErrorMessage(text.genericErrorText)
    } finally {
      setIsLoading(false)
    }
  }

  const submitExam = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (questions.some((question) => !answers[question.id])) {
      setErrorMessage(text.answerRequiredText)
      return
    }

    setErrorMessage('')
    setIsSubmitting(true)

    try {
      const response = await axios.post(submitEndpoint, {
        languageCode,
        answers: questions.map((question) => ({
          questionId: question.id,
          answerId: answers[question.id],
        })),
      })

      if (!response.data.success) {
        setErrorMessage(text.genericErrorText)
        return
      }

      setResult(response.data.value)
      setMode('result')
    } catch (error) {
      setErrorMessage(text.genericErrorText)
    } finally {
      setIsSubmitting(false)
    }
  }

  const restartExam = () => {
    setMode('intro')
    setQuestions([])
    setAnswers({})
    setCurrentIndex(0)
    setResult(null)
    setErrorMessage('')
  }

  const goToQuestion = (index: number) => {
    setErrorMessage('')
    setCurrentIndex(index)
  }

  const currentQuestion = questions[currentIndex]
  const isFirstQuestion = currentIndex === 0
  const isLastQuestion = currentIndex === questions.length - 1
  const remainingCount = questions.filter((q) => !answers[q.id]).length

  return (
    <ColorProvider value={{ color: 'green', type: 'solid' }}>
      <main className='digital-exam-page'>
        <div className='digital-exam-page__body'>
          <section className='digital-exam-page__card'>
            {mode === 'intro' && (
              <>
                <Heading level={2} size='l' className='digital-exam-page__title'>
                  {text.introTitle}
                </Heading>
                <p className='digital-exam-page__intro'>{text.introText}</p>
                <Button
                  text={isLoading ? text.loadingText : text.startButtonText}
                  onClick={() => {
                    void startExam()
                  }}
                  disabled={isLoading}
                />
              </>
            )}

            {mode === 'questions' && currentQuestion && (
              <form onSubmit={submitExam} className='digital-exam-page__form'>
                <div className='digital-exam-page__form-header'>
                  <Heading level={2} size='l' className='digital-exam-page__title'>
                    {text.introTitle}
                  </Heading>
                  <p className='digital-exam-page__count'>
                    {currentIndex + 1} / {questions.length}
                  </p>
                </div>

                <DigitalExamQuestionNav
                  questions={questions}
                  answers={answers}
                  currentIndex={currentIndex}
                  navigationLabel={text.questionNavigationLabel}
                  onQuestionSelect={goToQuestion}
                />

                <div className='digital-exam-page__questions'>
                  <DigitalExamQuestion
                    key={currentQuestion.id}
                    question={currentQuestion}
                    questionIndex={currentIndex}
                    selectedAnswerId={answers[currentQuestion.id]}
                    clearAnswerButtonText={text.clearAnswerButtonText}
                    onAnswerChange={(answerId) =>
                      setAnswers((currentAnswers) => ({
                        ...currentAnswers,
                        [currentQuestion.id]: answerId,
                      }))
                    }
                    onAnswerClear={() =>
                      setAnswers((currentAnswers) => {
                        const { [currentQuestion.id]: _removed, ...rest } =
                          currentAnswers
                        return rest
                      })
                    }
                  />
                </div>

                {isLastQuestion && remainingCount > 0 && (
                  <p className='digital-exam-page__remaining'>
                    {text.remainingQuestionsLabel}: {remainingCount}
                  </p>
                )}

                <div className='digital-exam-page__actions'>
                  {!isFirstQuestion && (
                    <Button
                      type='button'
                      text={text.previousButtonText}
                      onClick={() => goToQuestion(currentIndex - 1)}
                      outline
                    />
                  )}
                  {!isLastQuestion && (
                    <Button
                      type='button'
                      text={text.nextButtonText}
                      onClick={() => goToQuestion(currentIndex + 1)}
                      className='digital-exam-page__actions-next'
                    />
                  )}
                  {isLastQuestion && (
                    <Button
                      type='submit'
                      text={
                        isSubmitting ? text.loadingText : text.submitButtonText
                      }
                      disabled={isSubmitting}
                      className='digital-exam-page__actions-next'
                    />
                  )}
                </div>
              </form>
            )}

            {mode === 'result' && result && (
              <DigitalExamResults
                result={result}
                text={text}
                onRestart={restartExam}
              />
            )}

            {errorMessage && (
              <div className='digital-exam-page__error'>
                <ErrorMessage text={errorMessage} />
              </div>
            )}
          </section>
        </div>
      </main>
    </ColorProvider>
  )
}
